import type { MouseEvent } from 'react';
import type { CardProduct } from './types';
import { formatPrice } from './format';
import { StoreImage } from '../../ui/StoreImage';

/** Etiketten i hörnet. "Slut i lager" visas aldrig på kortet. */
export function cardBadge(p: CardProduct): string | null {
  if (p.compareAtPrice && p.compareAtPrice > p.price) {
    return `Spara ${formatPrice(p.compareAtPrice - p.price)}`;
  }
  if (p.tag?.trim()) return p.tag.trim();
  if (p.limitedEdition) return 'Limited Edition';
  if (p.stockStatus === 'low') return 'Få kvar';
  if (p.stockStatus === 'very_low') return 'Nästan slut';
  return null;
}

interface Props {
  product: CardProduct;
  /** Används om appen navigerar med en router istället för vanliga länkar */
  onSelect?: (product: CardProduct) => void;
}

export function ProductCard({ product, onSelect }: Props) {
  const badge = cardBadge(product);
  const hasCompare = !!product.compareAtPrice && product.compareAtPrice > product.price;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!onSelect) return;
    e.preventDefault();
    onSelect(product);
  };

  return (
    <a className="cn-card" href={product.href} onClick={handleClick}>
      <div className="cn-card__media">
        {/* En enda bild: bild 1 i galleriet. Ingen sammanfogning, inget svep.
            Tom alt-text: namnet står redan i länken. */}
        {product.imageUrl && <StoreImage src={product.imageUrl} width={640} alt="" loading="lazy" decoding="async" />}
        {badge && <span className="cn-badge">{badge}</span>}
      </div>
      <div className="cn-card__body">
        <h3 className="cn-card__title">{product.name}</h3>
        {product.subtitle && <p className="cn-card__sub">{product.subtitle}</p>}
        <p className="cn-card__price">
          <span>{product.priceFrom && <span className="cn-card__from">från </span>}{formatPrice(product.price)}</span>
          {/* Jämförpriset är delarnas priser, inte ett tidigare pris (prisinformationslagen). */}
          {hasCompare && !product.priceFrom && (
            <span className="cn-card__compare">
              <s>{formatPrice(product.compareAtPrice as number)}</s> var för sig
            </span>
          )}
        </p>
      </div>
    </a>
  );
}
